import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';

import { User } from '~/auth/model/user';
import { UserService } from '~/auth/services/user/user.service';
import { HeaderVariantEnum } from '~/component/header/enums/variant.enum';
import { MenuItemInterface } from '~/component/header/login/menu/interfaces/menu-item.interface';
import { LogoTypeEnum } from '~/component/logo/enums/logo-type.enum';
import { TabBarDistributionEnum } from '~/component/navigation-tab-bar/enums/navigation-tab-bar-distribution.enum';
import { NavigationTabBarButtonConfigInterface } from '~/component/navigation-tab-bar/interfaces/button.config.interface';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { TalkService } from '~/messaging/services/talk.service';
import { BuildNavigationTabBarButtonConfigInterface } from '~/organizer/shared/components/shared/layouts/dashboard/header/interfaces/build-tab-bar-buttons-config.interface';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { organizerStatus } from '~/organizer/enums/organizer-status.enum';
import { AlertModalComponent } from '~/component/modal/alert/alert.component';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { VtmnPlayButtonVariantStyleEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant-style.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { APP_STORAGE } from '~/app.token';
import { environment } from '~environments/environment';
import { OrganizationsByRoleInterface } from '@sport-activities/sdk';

const USER_ORGANIZATIONS = 'userOrganizations';

@Component({
  selector: 'app-organizer-shared-layout-dashboard-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class OrganizerSharedLayoutDashboardHeaderComponent
  implements OnInit, OnDestroy
{
  @ViewChild('accountModal') public accountModal: AlertModalComponent;
  @ViewChild('maintenanceModal') public maintenanceModal: AlertModalComponent;
  @ViewChild('organizerFedModal') public organizerFedModal: AlertModalComponent;
  private readonly subscriptions = new Subscription();
  private readonly tabBarButtonsConfig: BuildNavigationTabBarButtonConfigInterface[] =
    [
      {
        iconName: VtmnPlayIconTypeEnum.Calendar,
        isActive: true,
        isConversion: false,
        routeName: '/organizer.planning.planning',
        textKey: 'organizer.header.tab_bar.planning',
      },
      {
        iconName: VtmnPlayIconTypeEnum.Message,
        isActive: false,
        isConversion: false,
        routeName: '/organizer.messaging.inbox',
        textKey: 'organizer.header.tab_bar.messages',
      },
      {
        iconName: VtmnPlayIconTypeEnum.Add,
        isActive: false,
        isConversion: false,
        routeName: '/organizer.timeslot.schedule',
        textKey: 'organizer.header.tab_bar.create_activity',
      },
      {
        iconName: VtmnPlayIconTypeEnum.ListCheck,
        isActive: false,
        isConversion: false,
        routeName: '/organizer.activity.list',
        textKey: 'organizer.header.tab_bar.activities',
      },
    ];

  public readonly vtmnPlayIconTypeEnum = VtmnPlayIconTypeEnum;
  public readonly organizerLogo = LogoTypeEnum.OrganizerDark;
  public readonly organizerVariant = HeaderVariantEnum.Organizer;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonStyle = VtmnPlayButtonVariantStyleEnum;
  public readonly toggleNameEnum = ToggleNameEnum;

  public distribution = TabBarDistributionEnum.Even;
  public headerDropdownMenuItems: MenuItemInterface[][] = [];
  public homeLink: string;
  public isTabletDevice: boolean;
  public tabBarButtons: NavigationTabBarButtonConfigInterface[] = [];
  public unreadMessages = 0;
  public user: User;
  public stripeRedirectUrl: string;
  public modalAccountSecondaryButton: boolean;
  public organizationStatus: string;
  public onboardingUrl: string;
  private modalUrlRedirection = '/';
  public userOrganizations: OrganizationsByRoleInterface;

  constructor(
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly toggleService: ToggleService,
    private readonly translateService: TranslateService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly userService: UserService,
    private readonly talkService: TalkService,
    private readonly organizationService: OrganizerSharedOrganizationService,
    private readonly matchMediaService: MatchMediaService,
    private readonly redirectionService: RedirectionService,
    public readonly organizerActivityService: OrganizerSharedActivityService,
    private readonly sanitized: DomSanitizer
  ) {}

  private get isUserFed(): boolean {
    return this.userService.getUser()?.providerName === 'FED';
  }

  private get isInvalidStripeAccount(): boolean {
    return (
      this.organizationService.organization?.paymentProviderStatus !== 'LIVE' &&
      !this.toggleService.isEnabled(ToggleNameEnum.AllowExternalPayment)
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    if (
      this.toggleService.isEnabled(ToggleNameEnum.OrganizerDashboardMaintenance)
    ) {
      setTimeout(() => this.maintenanceModal.show(), 0);
    }

    this.userOrganizations = JSON.parse(
      this.storage.getItem(USER_ORGANIZATIONS)
    );

    this.user = this.userService.getUser();

    this.subscriptions.add(
      this.matchMediaService.isTabletDevice().subscribe((isTabletDevice) => {
        this.isTabletDevice = isTabletDevice;
      })
    );
    if (
      !this.isUserFed &&
      this.organizationService.organization?.paymentProvider
    ) {
      if (this.isInvalidStripeAccount) {
        this.subscriptions.add(
          this.organizationService
            .getStripeRedirectionUrl(
              this.organizationService.organization?.identifier,
              window.location.href
            )
            .subscribe({
              next: (data) => {
                if (data?.url) {
                  this.stripeRedirectUrl = data?.url;
                  this.buildHeaderDropdownMenuItems();
                  this.buildTabBarButtons();
                }
              },
              error: () => {
                this.setDefaultStripeRedirectUrl();
              },
            })
        );
      } else {
        this.setDefaultStripeRedirectUrl();
      }
    }

    if (!this.organizationService.organization?.paymentProvider) {
      this.subscriptions.add(
        this.organizationService
          .getOrganizationOnboardingUrl(
            this.organizationService.organization?.identifier
          )
          .subscribe((response) => {
            this.onboardingUrl = response.url;
          })
      );
    }

    this.buildHeaderDropdownMenuItems();
    this.buildTabBarButtons();
    this.buildHomeLink();
    this.modalStatusHandler();
    this.setUnreadNotificationListener();

    this.organizerActivityService
      .getActivitiesByOrganizationId(
        this.organizationService.organization?.identifier
      )
      .subscribe((activities) => {
        if (this.isUserFed && !this.organizationService.organization) {
          setTimeout(() => this.organizerFedModal.show(), 0);
        }

        if (
          (this.isValidAccount && activities.total === 0 && !this.isUserFed) ||
          (!this.isValidAccount && !this.isUserFed)
        ) {
          setTimeout(() => this.accountModal.show(), 0);
        }
      });
  }

  public get organizerStatus(): string {
    return this.organizationService.organization?.status
      .toLowerCase()
      .replace('_', '');
  }

  private get isValidAccount(): boolean {
    return (
      this.organizationService.organization?.status === organizerStatus.LIVE ||
      this.organizationService.organization?.status ===
        organizerStatus.VALIDATED
    );
  }

  private get incompleteOnboardedAccount(): boolean {
    return (
      this.organizationService.organization?.status ===
        organizerStatus.DISABLED ||
      this.organizationService.organization?.status ===
        organizerStatus.IN_PROGRESS
    );
  }

  public confirmModalHandler(): Promise<boolean> {
    if (this.onboardingUrl && this.incompleteOnboardedAccount) {
      window.location.href = this.onboardingUrl;
      return;
    }
    return this.redirectionService.redirect(this.modalUrlRedirection);
  }

  public checkOrganizationStatus(event: MouseEvent): Promise<boolean> {
    if (!this.isValidAccount) {
      event.preventDefault();
      this.accountModal.show();
      return;
    }
    return this.redirectionService.redirect(this.modalUrlRedirection);
  }

  private modalStatusHandler(): void {
    if (this.isValidAccount) {
      this.modalUrlRedirection = '/organizer.activity-creation.welcome';
      this.modalAccountSecondaryButton = true;
    }
  }

  public maintenanceModalHandler(): void {
    this.redirectionService.redirect('/');
  }

  public organizerFedModalHandler(): void {
    window.location.href = environment.applications.partner.onboardingRedirect;
  }

  private handleUnreadNotification(unreadNumber): void {
    this.unreadMessages = unreadNumber;

    const messageButton = this.tabBarButtons.find(
      (button) => button.iconName === VtmnPlayIconTypeEnum.Message
    );

    if (messageButton) {
      messageButton.totalNotifications = this.unreadMessages;
    }
  }

  private incomesMenuUrl(): MenuItemInterface {
    const label =
      this.organizationService?.organization?.paymentProviderIdentifier &&
      this.organizationService?.organization?.paymentProviderStatus === 'LIVE'
        ? 'organizer.header.login_menu.management.incomes'
        : 'organizer.header.login_menu.management.incomes.activation';
    const menu = {
      label: this.translateService.instant(label),
      icon: VtmnPlayIconTypeEnum.Wallet,
    };

    return {
      ...menu,
      href: this.stripeRedirectUrl,
    };
  }

  private setDefaultStripeRedirectUrl(): void {
    this.stripeRedirectUrl = this.organizationService?.organization
      ?.paymentProviderIdentifier
      ? `https://dashboard.stripe.com/connect/accounts/${this.organizationService?.organization?.paymentProviderIdentifier}`
      : 'https://dashboard.stripe.com/settings/connect';
  }

  private setUnreadNotificationListener(): void {
    this.subscriptions.add(
      this.talkService
        .createCurrentSession()
        .pipe(
          map(() => {
            this.talkService
              .onNotification$()
              .subscribe((unread) => this.handleUnreadNotification(unread));
          })
        )
        .subscribe()
    );
  }

  public buildHeaderDropdownMenuItems(): void {
    const mainItems: MenuItemInterface[] = [];

    if (this.toggleService.isEnabled(ToggleNameEnum.OrganizerProfileViewLink)) {
      mainItems.push({
        label: this.translateService.instant(
          'organizer.header.login_menu.management.see_my_profile'
        ),
        icon: VtmnPlayIconTypeEnum.Store,
        routerLink: this.localizeRouterService.translateRoute([
          '/attendee.organizer/',
          this.organizationService.organization?.identifier,
          this.organizationService.organization?.name,
          '/attendee.activities',
        ]),
        queryParams: { openInNewTab: true },
      });
    }

    if (this.isUserFed) {
      mainItems.push({
        label: this.translateService.instant(
          'organizer.header.login_menu.management.my_account'
        ),
        icon: VtmnPlayIconTypeEnum.User,
        href: environment.applications.partner.onboardingRedirect,
      });
    }

    if (
      !this.isUserFed &&
      this.toggleService.isEnabled(ToggleNameEnum.OrganizerInvoicesLink)
    ) {
      mainItems.push({
        label: this.translateService.instant(
          'organizer.header.login_menu.management.my_invoices'
        ),
        icon: VtmnPlayIconTypeEnum.Bill,
        routerLink: this.localizeRouterService.translateRoute([
          '/organizer.invoices.payout-invoices',
        ]),
      });
    }

    if (
      !this.toggleService.isEnabled(ToggleNameEnum.AllowExternalPayment) &&
      !this.isUserFed
    ) {
      mainItems.push(this.incomesMenuUrl());
    }

    if (this.toggleService.getVariant(ToggleNameEnum.LegalTermsSalesUrl, '')) {
      mainItems.push({
        label: this.translateService.instant(
          'organizer.header.login_menu.help_center.tos'
        ),
        icon: VtmnPlayIconTypeEnum.File,
        routerLink: this.toggleService.getVariant(
          ToggleNameEnum.LegalTermsSalesUrl,
          ''
        ),
      });
    }

    this.headerDropdownMenuItems = [
      mainItems,
      [
        {
          label: this.translateService.instant(
            'organizer.header.login_menu.other.leave'
          ),
          icon: VtmnPlayIconTypeEnum.ExternalLink,
          ...(this.toggleService.isEnabled(
            ToggleNameEnum.OrganizerLoginMenuInternalLeaveUrl
          )
            ? {
                routerLink:
                  this.localizeRouterService.translateRoute(
                    '/attendee.homepage'
                  ),
              }
            : {
                href: this.translateService.instant(
                  'organizer.header.login_menu.other.leaveUrl'
                ),
              }),
        },
      ],
    ];

    if (
      this.userOrganizations[0] &&
      this.userOrganizations[0]?.organizations?.length > 1
    ) {
      const currentOrganization = JSON.parse(
        this.storage.getItem('currentOrganization')
      );

      const rawLabel = this.translateService.instant(
        'organizer.header.login_menu.switch_organization',
        { organizationName: currentOrganization?.name }
      );

      const safeLabel = this.sanitized.bypassSecurityTrustHtml(rawLabel);

      this.headerDropdownMenuItems.push([
        {
          label: safeLabel,
          icon: VtmnPlayIconTypeEnum.Refresh,
          routerLink: this.localizeRouterService.translateRoute([
            '/organizer.organization-choice',
          ]),
        },
      ]);
    }
  }

  public buildHomeLink(): void {
    this.homeLink = this.localizeRouterService
      .translateRoute('/organizer.planning.planning')
      .toString();
  }

  public buildTabBarButtons(): void {
    this.tabBarButtons = this.tabBarButtonsConfig.map((item) => {
      if (item.routeName) {
        item.routerLink = this.localizeRouterService
          .translateRoute(item.routeName)
          .toString();
        if (item.routerLink) {
          item.isActive = window.location.pathname.includes(item.routerLink);
        }
      }

      if (item.isConversion) {
        return {
          ...item,
          conversionAriaLabel: this.translateService.instant(
            item.conversionAriaLabelKey
          ),
        };
      }

      return {
        ...item,
        text: this.translateService.instant(item.textKey),
      };
    });
  }
}
